import { z } from 'zod';

export const CourseProfileLevelSchema = z.enum([
  'nt1',
  'nt2',
  '1basico',
  '2basico',
  '3basico',
  '4basico',
  '5basico',
  '6basico',
  '7basico',
  '8basico',
]);

export const LearningStyleTagSchema = z.enum([
  'visual',
  'kinestesico',
  'auditivo',
  'lectoescritor',
  'colaborativo',
  'individual',
  'gamificado',
  'proyecto',
]);

export const VocalRangeTagSchema = z.enum(['children', 'adolescent', 'mixed']);

/** Limpia huecos undefined/null — evita "expected string, received undefined". */
function scrubStringList(val: unknown, maxLen = 200): string[] {
  if (!Array.isArray(val)) return [];
  return val
    .filter((item) => item != null)
    .map((item) => String(item).trim())
    .filter(Boolean)
    .map((item) => item.slice(0, maxLen));
}

const STYLE_SET = new Set(LearningStyleTagSchema.options);

export const InstrumentalProfileSchema = z.object({
  instrumentsAvailable: z.preprocess(
    (val) => scrubStringList(val, 120),
    z.array(z.string().trim().max(120)).default([]),
  ),
  vocalRange: VocalRangeTagSchema.optional(),
});

export const CourseProfileSchema = z.object({
  id: z.preprocess(
    (v) => (v == null || String(v).trim() === '' ? 'course' : v),
    z.string().trim().min(1).max(96),
  ),
  displayName: z.preprocess(
    (v) => (v == null || String(v).trim() === '' ? 'Curso' : v),
    z.string().trim().min(1).max(120),
  ),
  schoolName: z.preprocess(
    (v) => (v == null || String(v).trim() === '' ? undefined : v),
    z.string().trim().max(120).optional(),
  ),
  subjectId: z.string().trim().min(1).max(48),
  subjectTag: z.string().trim().min(1).max(48),
  additionalSubjectIds: z.preprocess(
    (val) => scrubStringList(val, 48),
    z.array(z.string().trim().max(48)).default([]),
  ),
  level: CourseProfileLevelSchema,
  interests: z.preprocess((val) => scrubStringList(val, 120), z.array(z.string().trim().max(120)).default([])),
  learningStyle: z.preprocess((val) => {
    return scrubStringList(val, 32).filter((item) => STYLE_SET.has(item as (typeof LearningStyleTagSchema.options)[number]));
  }, z.array(LearningStyleTagSchema).default([])),
  strengths: z.preprocess((val) => scrubStringList(val, 200), z.array(z.string().trim().max(200)).default([])),
  difficulties: z.preprocess((val) => scrubStringList(val, 200), z.array(z.string().trim().max(200)).default([])),
  instrumentalProfile: InstrumentalProfileSchema.optional(),
});

export type CourseProfileZod = z.infer<typeof CourseProfileSchema>;

/** Sanitiza un perfil crudo (localStorage / cliente) antes de enviarlo al API. */
export function sanitizeCourseProfileForRequest(raw: unknown): CourseProfileZod | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  // El logo es solo para impresión local; no viaja a prompts/API.
  const { schoolLogoDataUrl: _logo, ...withoutLogo } = raw as Record<string, unknown>;
  void _logo;
  const parsed = CourseProfileSchema.safeParse(withoutLogo);
  return parsed.success ? parsed.data : undefined;
}
